import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTools, FaHandshake, FaAward, FaClock } from 'react-icons/fa';
import { Link } from 'react-router-dom';

function Home() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Example slides
  const slides = [
    {
      id: 1,
      title: "Welcome to Griffith & McKee",
      text: "Quality contracting services for any project.",
      image: "./images/carousel1.png"
    },
    {
      id: 2,
      title: "Trusted Professionals",
      text: "Years of expertise in home renovation and construction.",
      image: "./images/carousel2.png"
    },
    {
      id: 3,
      title: "Satisfaction Guaranteed",
      text: "We stand by our work, always.",
      image: "./images/carousel3.png"
    }
  ];

  // Stats data
  const stats = [
    { value: 40, label: "Projects Completed", prefix: "+" },
    { value: 20, label: "Years Experience", suffix: "+" },
    { value: 98, label: "Client Satisfaction", suffix: "%" },
    { value: 2, label: "Team Members", prefix: "" }
  ];

  // Features data
  const features = [
    {
      icon: <FaTools />,
      title: "Expert Craftsmanship",
      description: "Precision and skill in every project we undertake"
    },
    {
      icon: <FaHandshake />,
      title: "Client-First Approach",
      description: "Your vision is our priority"
    },
    {
      icon: <FaAward />,
      title: "Award-Winning Service",
      description: "Recognized excellence in contracting"
    },
    {
      icon: <FaClock />,
      title: "Timely Delivery",
      description: "We value your time and stick to schedules"
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      text: "They were very friendly, knowledgeable and professional. I will definitely use them again!",
      author: "Guyonna B.",
      role: "Homeowner"
    },
    {
      text: "Excellent company! They were highly responsive.",
      author: "Jacky C.",
      role: "Homeowner"
    },
    {
      text: "Very friendly and knowledgeable guys. Very professional from the first call.",
      author: "Jason H.",
      role: "Homeowner"
    }
  ];

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    const handleScroll = () => {
      const scrolled = window.scrollY;
      setIsVisible(scrolled > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      clearInterval(slideInterval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [slides.length]);

  return (
    <div className="home-container">
      {/* Hero Section */}
      <motion.section 
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="hero-slider">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`hero-slide ${index === currentSlide ? 'active-slide' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="hero-content">
                <motion.h1
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  {slide.title}
                </motion.h1>
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  {slide.text}
                </motion.p>
                <Link to="/projects">
                  <motion.button
                    className="cta-button"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.9 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Get Started
                  </motion.button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Features Grid */}
      <section className="features-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Why Choose Us
        </motion.h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="stat-card"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <h3>
                {stat.prefix}
                {stat.value}
                {stat.suffix}
              </h3>
              <p>{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          What Our Clients Say
        </motion.h2>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="testimonial-card"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              <p>"{testimonial.text}"</p>
              <div className="testimonial-author">
                <h4>{testimonial.author}</h4>
                <span>{testimonial.role}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Ready to Start Your Project?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Let's discuss your vision and bring it to life.
        </motion.p>
        <Link to="/contact">
          <motion.button
            className="cta-button"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Contact Us
          </motion.button>
        </Link>
      </section>
    </div>
  );
}

export default Home;